import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Container,
  Typography,
  TextField,
  Button,
  Box,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Paper,
} from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { C } from '../theme';
import { countries, getCitiesByCountry } from '../data/locations';
import LocationPicker from '../components/LocationPicker';

interface BusinessAd {
  id: number;
  title: string;
  description: string;
  price: number;
  country_id: string;
  city_id: string;
  business_type: string;
  annual_revenue?: number;
  employee_count?: number;
  established_year?: number;
  author_id: number;
}

const businessTypes = [
  { value: 'restaurant_hospitality', label: 'Restoran i ugostiteljstvo' },
  { value: 'retail_services', label: 'Trgovina i usluge' },
  { value: 'manufacturing', label: 'Proizvodnja' },
  { value: 'technology_it', label: 'Tehnologija i IT' },
  { value: 'healthcare', label: 'Zdravstvo' },
  { value: 'education', label: 'Obrazovanje' },
  { value: 'transport_logistics', label: 'Transport i logistika' },
  { value: 'real_estate', label: 'Nekretnine' },
  { value: 'finance_banking', label: 'Finansije i bankarstvo' },
  { value: 'other', label: 'Ostalo' },
];

const EditBusinessAd: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    price: '',
    country_id: '',
    city_id: '',
    business_type: '',
    annual_revenue: '',
    employee_count: '',
    established_year: '',
  });

  const [cities, setCities] = useState<any[]>([]);
  const [location, setLocation] = useState<{ lat: number; lng: number } | null>(null);

  useEffect(() => {
    fetchAd();
    // Initialize cities based on the ad's country
    if (formData.country_id) {
      setCities(getCitiesByCountry(Number(formData.country_id)));
    }
  }, [id]);

  const fetchAd = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`/api/business-ads/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const ad = response.data;
      
      // Check if user owns this ad
      if (ad.author_id !== user?.id) {
        setError('Nemate dozvolu da uređujete ovaj oglas');
        setLoading(false);
        return;
      }

      setFormData({
        title: ad.title || '',
        description: ad.description || '',
        price: ad.price?.toString() || '',
        country_id: ad.country_id?.toString() || '',
        city_id: ad.city_id?.toString() || '',
        business_type: ad.business_type || '',
        annual_revenue: ad.annual_revenue?.toString() || '',
        employee_count: ad.employee_count?.toString() || '',
        established_year: ad.established_year?.toString() || '',
      });

             // Set cities for the selected country
       if (ad.country_id) {
         setCities(getCitiesByCountry(Number(ad.country_id)));
       }

       if (ad.latitude != null && ad.longitude != null) {
         setLocation({ lat: Number(ad.latitude), lng: Number(ad.longitude) });
       }
    } catch (error) {
      console.error('Error fetching ad:', error);
      setError('Greška pri učitavanju oglasa');
    } finally {
      setLoading(false);
    }
  };

  const fetchCities = (countryId: string) => {
    const citiesForCountry = getCitiesByCountry(Number(countryId));
    setCities(citiesForCountry);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name as string]: value,
    }));
  };

  const handleSelectChange = (e: any) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name as string]: value,
    }));

    // If country changes, reset city and set new cities
    if (name === 'country_id') {
      setFormData(prev => ({ ...prev, city_id: '' }));
      if (value) {
        setCities(getCitiesByCountry(Number(value)));
      } else {
        setCities([]);
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setSaving(true);

    try {
      const token = localStorage.getItem('token');
      
      // Validate required fields
      if (!formData.title || !formData.price || !formData.country_id || !formData.city_id || !formData.business_type) {
        setError('Molimo popunite sva obavezna polja');
        setSaving(false);
        return;
      }

      const updateData = {
        title: formData.title.trim(),
        description: formData.description.trim(),
        price: parseFloat(formData.price),
        country_id: parseInt(formData.country_id),
        city_id: parseInt(formData.city_id),
        business_type: formData.business_type,
        annual_revenue: formData.annual_revenue ? parseFloat(formData.annual_revenue) : null,
        employee_count: formData.employee_count ? parseInt(formData.employee_count) : null,
        established_year: formData.established_year ? parseInt(formData.established_year) : null,
        latitude: location ? location.lat : null,
        longitude: location ? location.lng : null,
      };

              await axios.patch(`/api/business-ads/${id}`, updateData, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setSuccess('Oglas je uspešno ažuriran');
      
      // Redirect after 1.5 seconds
      setTimeout(() => {
        navigate(`/business/${id}`);
      }, 1500);
    } catch (error: any) {
      console.error('Error updating ad:', error);
      setError(error.response?.data?.message || 'Greška pri ažuriranju oglasa');
      setSaving(false); // keep the button spinning on success until the redirect
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error && error.includes('dozvolu')) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <Alert severity="error" sx={{ maxWidth: 600 }}>
          {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ pt: '110px', pb: 8, backgroundColor: C.bg, minHeight: '100vh' }}>
      <Container maxWidth="md">
        <Paper sx={{ p: 4, borderRadius: 2 }}>
          <Typography variant="h4" sx={{ mb: 4, fontWeight: 600, textAlign: 'center' }}>
            Uredi biznis oglas
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mb: 3 }}>
              {success}
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Naslov oglasa"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  required
                  variant="outlined"
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Opis biznisa"
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  multiline
                  rows={4}
                  variant="outlined"
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Cena (€)"
                  name="price"
                  type="number"
                  value={formData.price}
                  onChange={handleInputChange}
                  required
                  variant="outlined"
                  inputProps={{ min: 0, step: 0.01 }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControl fullWidth required>
                  <InputLabel>Tip biznisa</InputLabel>
                  <Select
                    name="business_type"
                    value={formData.business_type}
                    onChange={handleSelectChange}
                    label="Tip biznisa"
                  >
                    {businessTypes.map((type) => (
                      <MenuItem key={type.value} value={type.value}>
                        {type.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControl fullWidth required>
                  <InputLabel>Država</InputLabel>
                  <Select
                    name="country_id"
                    value={formData.country_id}
                    onChange={handleSelectChange}
                    label="Država"
                  >
                    {countries.map((country) => (
                      <MenuItem key={country.id} value={country.id}>
                        {country.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControl fullWidth required>
                  <InputLabel>Grad</InputLabel>
                  <Select
                    name="city_id"
                    value={formData.city_id}
                    onChange={handleSelectChange}
                    label="Grad"
                    disabled={!formData.country_id}
                  >
                    {cities.map((city) => (
                      <MenuItem key={city.id} value={city.id}>
                        {city.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Godišnji prihod (€)"
                  name="annual_revenue"
                  type="number"
                  value={formData.annual_revenue}
                  onChange={handleInputChange}
                  variant="outlined"
                  inputProps={{ min: 0, step: 0.01 }}
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Broj zaposlenih"
                  name="employee_count"
                  type="number"
                  value={formData.employee_count}
                  onChange={handleInputChange}
                  variant="outlined"
                  inputProps={{ min: 0 }}
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Godina osnivanja"
                  name="established_year"
                  type="number"
                  value={formData.established_year}
                  onChange={handleInputChange}
                  variant="outlined"
                  inputProps={{ min: 1900, max: new Date().getFullYear() }}
                />
              </Grid>

              <Grid item xs={12}>
                <Typography variant="h6" sx={{ mb: 1, fontWeight: 600, color: C.white }}>
                  Lokacija na mapi
                </Typography>
                <Typography variant="body2" sx={{ mb: 2, color: C.muted }}>
                  Opciono. Lokacija je vidljiva samo vama i investitorima.
                </Typography>
                <LocationPicker value={location} onChange={setLocation} />
              </Grid>

              <Grid item xs={12}>
                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', mt: 2 }}>
                  <Button
                    type="button"
                    variant="outlined"
                    onClick={() => navigate(`/business/${id}`)}
                    disabled={saving}
                    sx={{ px: 4, py: 1.5 }}
                  >
                    Otkaži
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    disabled={saving}
                    sx={{ px: 4, py: 1.5 }}
                  >
                    {saving ? <CircularProgress size={24} sx={{ color: C.onGold }} /> : 'Sačuvaj izmene'}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </form>
        </Paper>
      </Container>
    </Box>
  );
};

export default EditBusinessAd;
